import "./styles.css";
import "./about/styles.css";
import "./about/models.css";
import "./about/editorial.css";
import "./about/story.css";
import "./about/site-chrome.css";
import { initializeEmailCopyButtons } from "./copy-email";
import { loginEntryUrl, trialEntryUrl } from "./site-config";

document.querySelectorAll<HTMLAnchorElement>("[data-trial-link]").forEach((link) => {
  link.href = trialEntryUrl;
});

document.querySelectorAll<HTMLAnchorElement>("[data-login-link]").forEach((link) => {
  link.href = loginEntryUrl;
});

initializeEmailCopyButtons();

const dialog = document.querySelector<HTMLDialogElement>("#contact-dialog");
const dialogTitle = document.querySelector<HTMLElement>("#dialog-title");
const dialogEyebrow = document.querySelector<HTMLElement>("#dialog-eyebrow");
const dialogDescription = document.querySelector<HTMLElement>("#dialog-description");
const closeDialogButton = document.querySelector<HTMLButtonElement>(".close-dialog");
let contactTrigger: HTMLButtonElement | null = null;

const closeContact = () => dialog?.close();

if (dialog && dialogTitle && dialogEyebrow && dialogDescription && closeDialogButton) {
  document.querySelectorAll<HTMLButtonElement>("[data-contact]").forEach((button) => {
    button.addEventListener("click", () => {
      const type = button.dataset.contact;
      contactTrigger = button;
      dialogTitle.textContent = type === "join" ? "一起，做好产品。" : "聊聊新的合作。";
      dialogEyebrow.textContent = type === "join" ? "JOIN INFLOWMAX" : "GROW WITH INFLOWMAX";
      dialogDescription.textContent = "选择微信或飞书，与 InflowMax 团队联系。";
      dialog.showModal();
      document.body.classList.add("modal-open");
    });
  });

  closeDialogButton.addEventListener("click", closeContact);
  dialog.addEventListener("click", (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const outsideDialog = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    if (outsideDialog) closeContact();
  });
  dialog.addEventListener("close", () => {
    document.body.classList.remove("modal-open");
    contactTrigger?.focus({ preventScroll: true });
  });
}

type ModelName = "gpt" | "claude" | "gemini" | "qwen";

const modelWorld = document.querySelector<HTMLElement>(".model-world");

if (modelWorld) {
  const modelButtons = Array.from(modelWorld.querySelectorAll<HTMLButtonElement>("[data-model]"));
  const modelNames: Record<ModelName, string> = { gpt: "GPT", claude: "Claude", gemini: "Gemini", qwen: "阿里千问" };
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const motionToggle = modelWorld.querySelector<HTMLButtonElement>(".motion-toggle");
  const announcement = modelWorld.querySelector<HTMLElement>(".model-announcement");
  let userPaused = false;
  let onScreen = false;
  let hovering = false;
  let focused = false;
  let reducedMotion = motionQuery.matches;
  let pulseTimer: number | undefined;
  let pointerFrame: number | undefined;

  const isModelName = (value: string | undefined): value is ModelName => Boolean(value && value in modelNames);

  const updateMotion = () => {
    modelWorld.classList.toggle("motion-stopped", userPaused || !onScreen || document.hidden || reducedMotion);
    modelWorld.classList.toggle("user-paused", userPaused);
    modelWorld.classList.toggle("reduced-motion", reducedMotion);
    if (motionToggle) {
      motionToggle.setAttribute("aria-pressed", String(userPaused));
      motionToggle.setAttribute("aria-label", userPaused ? "播放动画" : "暂停动画");
      motionToggle.hidden = reducedMotion;
    }
  };

  const activateModel = (model: ModelName, announce = false, pulse = false) => {
    modelWorld.dataset.active = model;
    modelButtons.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.model === model)));
    if (announce && announcement) announcement.textContent = `已选择 ${modelNames[model]} 联动演示`;
    if (pulse && !reducedMotion && !userPaused) {
      modelWorld.classList.remove("has-pulse");
      void modelWorld.offsetWidth;
      modelWorld.classList.add("has-pulse");
      window.clearTimeout(pulseTimer);
      pulseTimer = window.setTimeout(() => modelWorld.classList.remove("has-pulse"), 1250);
    }
  };

  modelButtons.forEach((button) => {
    const activateButtonModel = (announce = false, pulse = false) => {
      if (isModelName(button.dataset.model)) activateModel(button.dataset.model, announce, pulse);
    };
    button.addEventListener("pointerenter", () => activateButtonModel());
    button.addEventListener("focus", () => activateButtonModel());
    button.addEventListener("click", () => activateButtonModel(true, true));
  });

  motionToggle?.addEventListener("click", () => {
    userPaused = !userPaused;
    updateMotion();
  });
  modelWorld.addEventListener("pointerenter", () => {
    hovering = true;
  });
  modelWorld.addEventListener("pointerleave", () => {
    hovering = false;
    if (pointerFrame) window.cancelAnimationFrame(pointerFrame);
    pointerFrame = undefined;
    modelWorld.style.setProperty("--pointer-x", "0px");
    modelWorld.style.setProperty("--pointer-y", "0px");
    modelWorld.style.setProperty("--tilt-x", "0deg");
    modelWorld.style.setProperty("--tilt-y", "0deg");
  });
  modelWorld.addEventListener("pointermove", (event) => {
    if (event.pointerType !== "mouse" || reducedMotion || userPaused) return;
    if (pointerFrame) window.cancelAnimationFrame(pointerFrame);
    pointerFrame = window.requestAnimationFrame(() => {
      const rect = modelWorld.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      modelWorld.style.setProperty("--pointer-x", `${x * 14}px`);
      modelWorld.style.setProperty("--pointer-y", `${y * 14}px`);
      modelWorld.style.setProperty("--tilt-x", `${-y * 3}deg`);
      modelWorld.style.setProperty("--tilt-y", `${x * 3}deg`);
      pointerFrame = undefined;
    });
  });
  modelWorld.addEventListener("focusin", () => {
    focused = true;
  });
  modelWorld.addEventListener("focusout", (event) => {
    focused = modelWorld.contains(event.relatedTarget as Node | null);
  });
  document.addEventListener("visibilitychange", updateMotion);
  motionQuery.addEventListener("change", (event) => {
    reducedMotion = event.matches;
    updateMotion();
  });

  const observer = new IntersectionObserver(
    (entries) => {
      onScreen = Boolean(entries[0]?.isIntersecting);
      updateMotion();
    },
    { threshold: 0.12 }
  );
  observer.observe(modelWorld);
  updateMotion();

  const cycleModels = () => {
    if (userPaused || !onScreen || document.hidden || reducedMotion || hovering || focused) return;
    const activeIndex = modelButtons.findIndex((button) => button.dataset.model === modelWorld.dataset.active);
    const nextButton = modelButtons[(activeIndex + 1) % modelButtons.length];
    if (nextButton && isModelName(nextButton.dataset.model)) activateModel(nextButton.dataset.model);
  };
  let cycleTimer = window.setInterval(cycleModels, 4400);

  window.addEventListener("pagehide", () => {
    window.clearInterval(cycleTimer);
    window.clearTimeout(pulseTimer);
    if (pointerFrame) window.cancelAnimationFrame(pointerFrame);
    observer.disconnect();
  });
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    observer.observe(modelWorld);
    window.clearInterval(cycleTimer);
    cycleTimer = window.setInterval(cycleModels, 4400);
    updateMotion();
  });
}
